import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import DraggableTable, { DraggableTableColumn } from "@/Components/Common/DraggableTable";
import { Button, Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, Row } from "@/Components/ui";
import { API, BACKEND_HOST, getUserInfo } from "@/api";
import type { UserInfo } from "@/api/type";
import { canManageSiteScopedCommerce } from "@/common/permissions";
import { dialogService } from "@/utils/dialogService";
type ReservationProductRow = {
    id: number;
    상품명: string;
    자리수: number | string;
    상태: string;
    요일정책?: string;
    평일?: number | string;
    금요일?: number | string;
    주말?: number | string;
    공휴일?: number | string;
};
const PRICE_KEYS: Array<keyof ReservationProductRow> = ["평일", "금요일", "주말", "공휴일"];
export default function ReservationProducts() {
    const navigate = useNavigate();
    const [data, setData] = useState<ReservationProductRow[]>([]);
    const [delRecord, setDelRecord] = useState<number | null>(null);
    const [modalDelete, setModalDelete] = useState(false);
    const [currentUser, setCurrentUser] = useState<UserInfo>(null);
    const canManageCommerce = useMemo(() => canManageSiteScopedCommerce(currentUser), [currentUser]);
    const guardOwnerAction = useCallback(async (action: () => void) => {
        if (!canManageCommerce) {
            await dialogService.handleAlert("지점 소유자 또는 최상위 관리자만 작업할 수 있습니다.");
            return;
        }
        action();
    }, [canManageCommerce]);
    const fetchProducts = useCallback(async () => {
        try {
            const { data: responseData } = await API.get(`${BACKEND_HOST}/package/items/?include_hidden=true`);
            setData(responseData || []);
        }
        catch (error) {
            console.error(error);
        }
    }, []);
    React.useEffect(() => {
        getUserInfo()
            .then((info) => setCurrentUser(info))
            .catch(() => setCurrentUser(null));
        fetchProducts();
    }, [fetchProducts]);
    const handleEdit = useCallback((id: number) => {
        guardOwnerAction(() => navigate(`/reservation-add-product/${id}`));
    }, [guardOwnerAction, navigate]);
    const handleCopy = useCallback((id: number) => {
        guardOwnerAction(() => navigate(`/reservation-add-product/${id}?copy=true`));
    }, [guardOwnerAction, navigate]);
    const handleDelete = useCallback((id: number) => {
        guardOwnerAction(() => {
            setDelRecord(id);
            setModalDelete(true);
        });
    }, [guardOwnerAction]);
    const deleteRecord = useCallback(async () => {
        if (!delRecord)
            return;
        try {
            await API.delete(`${BACKEND_HOST}/package/items/${delRecord}/`);
            setModalDelete(false);
            setDelRecord(null);
            await fetchProducts();
            await dialogService.handleAlert("Delete ok!");
        }
        catch (error: any) {
            await dialogService.handleAlert(error?.message ?? "Delete failed", { force: "error" });
        }
    }, [delRecord, fetchProducts]);
    const handleSaveOrder = useCallback(async (rows: ReservationProductRow[]) => {
        if (!canManageCommerce) {
            await dialogService.handleAlert("지점 소유자 또는 최상위 관리자만 순서를 저장할 수 있습니다.");
            await fetchProducts();
            return;
        }
        try {
            await API.post(`${BACKEND_HOST}/package/items/reorder/`, {
                ids: rows.map((row) => row.id),
            });
            await fetchProducts();
        }
        catch (error) {
            await fetchProducts();
            throw error;
        }
    }, [canManageCommerce, fetchProducts]);
    const renderPriceColumn = useCallback((row: ReservationProductRow) => {
        const groupedPolicy = row["요일정책"]?.split("\n").filter(Boolean).map((line) => ({
            label: line,
            value: "",
        }));
        if (groupedPolicy && groupedPolicy.length > 0) {
            return (<div className="max-w-[240px]" title={row["요일정책"]}>
        <div className="text-sm leading-5 text-slate-700" style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}>
          {groupedPolicy.map((item, index) => (<div key={`${row.id}-grouped-${index}`}>{item.label}</div>))}
        </div>
      </div>);
        }
        const rows = PRICE_KEYS.filter((key) => row[key] !== undefined && row[key] !== null && row[key] !== "").map((key) => ({
            label: key,
            value: row[key],
        }));
        const fullText = rows.map((item) => `${item.label}: ${item.value}`).join(" / ");
        if (!rows.length) {
            return <span className="text-slate-400">-</span>;
        }
        return (<div className="max-w-[240px]" title={fullText}>
        <div className="text-sm leading-5 text-slate-700" style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}>
          {rows.map((item) => (<div key={`${row.id}-${item.label}`}>{`${item.label}: ${item.value}`}</div>))}
        </div>
      </div>);
    }, []);
    const columns = useMemo<DraggableTableColumn<ReservationProductRow>[]>(() => [
        { key: "id", header: "ID", render: (row) => row.id },
        { key: "name", header: "상품명", render: (row) => row.상품명 },
        { key: "count", header: "자리수", render: (row) => row.자리수 },
        { key: "state", header: "상태", render: (row) => row.상태 },
        { key: "price", header: "요금", render: (row) => renderPriceColumn(row) },
        {
            key: "actions",
            header: "관리",
            className: "whitespace-nowrap",
            render: (row) => (<div className="flex gap-2">
            <Button size="sm" color="success" onClick={() => handleEdit(row.id)}>
              수정
            </Button>
            <Button size="sm" color="info" onClick={() => handleCopy(row.id)}>
              복사
            </Button>
            <Button size="sm" color="danger" onClick={() => handleDelete(row.id)}>
              삭제
            </Button>
          </div>),
        },
    ], [handleCopy, handleDelete, handleEdit, renderPriceColumn]);
    return (<React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="예약상품" pageTitle="예약"/>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h4 className="card-title mb-0 flex-grow-1">예약 상품 목록</h4>
                  <div>
                    <Button color="success" className="add-btn me-1" onClick={() => {
            guardOwnerAction(() => navigate("/reservation-add-product/0"));
        }} id="create-btn">
                      <i className="ri-add-line align-bottom me-1"></i> 생성
                    </Button>
                  </div>
                </CardHeader>

                <CardBody>
                  <DraggableTable data={data} getRowId={(row) => row.id} columns={columns} onReorder={setData} onSaveOrder={canManageCommerce ? handleSaveOrder : undefined} onSaveError={async (error: any) => {
            await dialogService.handleAlert(error?.response?.data?.error || error?.message || "순서 저장에 실패했습니다.");
        }} emptyText="예약 상품이 없습니다."/>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalDelete} onClose={() => setModalDelete(false)} centered variant="dialog">
        <ModalBody className="text-center p-5">
          <div className="mt-4">
            <h4 className="mb-3">Are you Sure ?</h4>
            <p className="text-muted mb-4">{`Are you Sure You want to Remove this Record(id:${delRecord}) ?`}</p>
            <div className="hstack gap-2 justify-content-center">
              <Button color="light" onClick={() => {
            setDelRecord(null);
            setModalDelete(false);
        }}>
                Cancel
              </Button>
              <Button color="danger" onClick={deleteRecord}>
                Delete
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>);
}
